import { Metric } from "@/pages/graphs/group-graphs";
import { useAppData } from "@/hooks/useAppData";
import React, { useEffect, useMemo, useState } from "react";
import { ApartmentStats, Convert, GetMonthlyApartmentStatsResponse } from "@/models/responses";
import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import YearOverYearChart from "@/pages/graphs/year-over-year-chart";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { useAuth } from "@/hooks/useAuth";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { useActiveApartmentEntities, useActiveApartmentOptions } from "@/store/slices/apartments";
import { aggregateApartmentStatsByGroup } from "@/utils/groupStatsAggregation";

interface GroupYearOverYearChartProps {
    selectedMetric: {
        label: string;
        value: Metric;
        formatter: (value: number) => string;
    };
    startDate: Date;
    endDate: Date;
}

// Traceability: FR IDs reference
// .verified/features/apartment-filters-graphs-and-database/spec.md. Group
// Year-over-Year sub-chart: per-group lines aggregated client-side from
// per-apartment stats with the filter applied (FR-009).
const GroupYearOverYearChart: React.FC<GroupYearOverYearChartProps> = ({ selectedMetric, startDate, endDate }) => {
    const { groupOptions, selectedGroup } = useAppData();
    const { setSelectedGroup } = useUserSettingsDispatch();
    const { user } = useAuth();
    const { predicates } = useApartmentFilters();
    const apartmentEntities = useActiveApartmentEntities();
    const apartmentOptions = useActiveApartmentOptions();

    const [apartmentStats, setApartmentStats] = useState<ApartmentStats[]>([]);
    const [months, setMonths] = useState<Date[]>([]);

    // Every active apartment ID; `apartments/stats-by-month` returns
    // per-apartment monthly stats aggregated per group client-side (FR-009).
    const activeApartmentIds = useMemo(() => {
        return apartmentOptions
            .filter((opt) => opt.value !== "0")
            .map((opt) => Number(opt.value))
            .filter((n) => !Number.isNaN(n));
    }, [apartmentOptions]);

    useEffect(() => {
        if (!user || activeApartmentIds.length === 0) {
            return;
        }

        const apartmentsParam = activeApartmentIds.join(",");
        const apartmentStatsUrl = `${apiUrl}${getApiBasePath()}/${user.isAdmin ? "admin/" : ""}apartments/stats-by-month?apartments=${apartmentsParam}&startDate=${startDate.toISOString().slice(0, 10)}&endDate=${endDate.toISOString().slice(0, 10)}`;

        fetch(apartmentStatsUrl, fetchOptions)
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.text();
            })
            .then((data) => {
                const stats: GetMonthlyApartmentStatsResponse = Convert.toGetMonthlyApartmentStatsResponse(data);
                setApartmentStats(stats.apartmentStats);
                setMonths(stats.months);
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: `Failed to fetch apartment stats: ${error.message}`,
                    variant: "destructive",
                });
            });
    }, [startDate, endDate, user, activeApartmentIds]);

    const selectedGroupStr = selectedGroup.toString();

    // Aggregate per group via the shared helper, then keep only the selected
    // group. No group selected, or none of its apartments survive the
    // predicate filter → empty stats array → chart shows its empty state.
    const groupStats = useMemo(() => {
        if (apartmentStats.length === 0 || months.length === 0) {
            return [];
        }
        const aggregated = aggregateApartmentStatsByGroup(apartmentStats, months, apartmentEntities, predicates);
        return aggregated
            .filter((row) => row.groupId === selectedGroup)
            .map((row) => ({
                date: months[row.monthIndex],
                value: row[selectedMetric.value],
            }));
    }, [apartmentStats, months, apartmentEntities, predicates, selectedGroup, selectedMetric]);

    return (
        <>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 mt-4 sm:mt-8">
                <h2 className={"font-semibold mb-8"}>{selectedMetric.label} - Year over Year Comparison</h2>
                <SearchableSelect
                    variant={"volt"}
                    selectedValue={selectedGroupStr}
                    onChange={(val) => setSelectedGroup(Number(val))}
                    placeholder={"Select Group"}
                    options={groupOptions.filter((option) => option.value !== "0").sort((a, b) => a.label.localeCompare(b.label))}
                    selectedValuePrefix={"Group"}
                    className="w-auto"
                />
            </div>
            <YearOverYearChart stats={groupStats} valueFormatter={selectedMetric.formatter} />
        </>
    );
};

export default GroupYearOverYearChart;
